import React from 'react';
import { ACCENT_HEX, ACCENT_SOFT, type JuniorAccent } from '../../lib/juniorTheme';
import type { RhythmPatternTimeline } from '../../lib/rhythm/rhythmPattern';
import type { RhythmAttackEvent } from '../../lib/rhythm/rhythmTiming';
import { figureShortLabel, type RhythmFigureId } from '../../lib/rhythm/rhythmTokens';
import { RhythmFigureIcon } from './RhythmFigureIcon';

export type FigureCellState = 'idle' | 'active' | 'hit' | 'late' | 'miss' | 'rest';

interface FigureGridProps {
  cells: RhythmPatternTimeline['cells'];
  activeCellIndex: number;
  /** Estado por celda durante la fase de juego del usuario. */
  cellStates?: FigureCellState[];
  accent?: JuniorAccent;
  /** Índices de celda tras los cuales dibujar separador de compás. */
  measureBreakAfter?: number[];
  /** Muestra solo un subconjunto de celdas (p. ej. un compás). */
  cellOffset?: number;
  cellCount?: number;
  compact?: boolean;
  hideLabels?: boolean;
}

function cellAccent(fig: RhythmFigureId): JuniorAccent {
  if (fig.startsWith('silencio')) return 'violet';
  if (fig === 'cuartina') return 'sky';
  if (fig === 'corchea_doble') return 'mint';
  if (fig === 'blanca') return 'sun';
  return 'coral';
}

function stateStyles(state: FigureCellState, hex: string, soft: string) {
  switch (state) {
    case 'active':
      return {
        background: `linear-gradient(160deg, ${soft} 0%, #fff 80%)`,
        border: `3px solid ${hex}`,
        boxShadow: `0 0 0 4px ${hex}40, 0 12px 32px -8px ${hex}70`,
        transform: 'scale(1.06)',
      };
    case 'hit':
      return {
        background: 'linear-gradient(160deg, #D1FAE5 0%, #fff 80%)',
        border: '3px solid #10B981',
        boxShadow: '0 8px 24px -8px #10B98180',
      };
    case 'late':
      return {
        background: 'linear-gradient(160deg, #FEF3C7 0%, #fff 80%)',
        border: '3px solid #F59E0B',
        boxShadow: '0 8px 24px -8px #F59E0B80',
      };
    case 'miss':
      return {
        background: 'linear-gradient(160deg, #FCE7F3 0%, #fff 80%)',
        border: '3px solid #F472B6',
        boxShadow: '0 8px 24px -8px #F472B680',
        opacity: 0.85,
      };
    case 'rest':
      return {
        background: 'rgba(107, 99, 133, 0.08)',
        border: '3px dashed #A79FC0',
      };
    default:
      return {
        background: 'rgba(255,255,255,0.94)',
        border: `2px solid ${hex}30`,
        boxShadow: `0 4px 16px -6px ${hex}25`,
      };
  }
}

export function attacksToCellStates(
  cells: RhythmPatternTimeline['cells'],
  attacks: readonly RhythmAttackEvent[]
): FigureCellState[] {
  return cells.map((cell) => {
    if (cell.isRest) return 'rest';

    const related = attacks.filter(
      (a) => a.startMs >= cell.startMs && a.startMs < cell.startMs + cell.durationMs
    );

    if (related.length === 0) return 'idle';
    if (related.some((a) => a.status === 'miss')) return 'miss';
    if (related.every((a) => a.status === 'hit')) return 'hit';
    if (related.some((a) => a.status === 'late')) return 'late';
    if (related.some((a) => a.status === 'hit')) return 'hit';
    return 'idle';
  });
}

export const FigureGrid: React.FC<FigureGridProps> = ({
  cells,
  activeCellIndex,
  cellStates,
  accent = 'sun',
  measureBreakAfter = [],
  cellOffset = 0,
  cellCount,
  compact = false,
  hideLabels = false,
}) => {
  const breakSet = new Set(measureBreakAfter);
  const slice = cells.slice(cellOffset, cellCount != null ? cellOffset + cellCount : undefined);
  const iconSize = compact ? 44 : 58;
  const cellClass = compact
    ? 'eco-figure-cell flex flex-col items-center justify-center rounded-xl p-2 transition-all duration-150 flex-1 min-w-0 max-w-[88px]'
    : 'eco-figure-cell flex flex-col items-center justify-center rounded-[1.25rem] p-3 sm:p-4 transition-all duration-150 w-[calc(50%-0.375rem)] sm:w-[calc(25%-0.75rem)] max-w-[140px]';

  return (
    <div
      className={
        compact
          ? 'flex gap-2 w-full max-w-md mx-auto justify-center items-stretch'
          : 'flex flex-wrap gap-3 sm:gap-4 w-full max-w-4xl mx-auto justify-center items-start'
      }
    >
      {slice.map((cell, i) => {
        const globalIndex = cellOffset + i;
        const figAccent = cellAccent(cell.figureId);
        const hex = ACCENT_HEX[figAccent];
        const soft = ACCENT_SOFT[figAccent];
        const state =
          cellStates?.[globalIndex] ??
          (globalIndex === activeCellIndex ? 'active' : cell.isRest ? 'rest' : 'idle');
        const styles = stateStyles(state, hex, soft);
        const showBar = breakSet.has(globalIndex);

        return (
          <React.Fragment key={globalIndex}>
            <div className={cellClass} style={styles}>
              <RhythmFigureIcon figureId={cell.figureId} size={iconSize} />
              {!hideLabels && (
                <span
                  className={`${compact ? 'mt-1 text-[9px]' : 'mt-2 text-[10px] sm:text-xs'} font-extrabold text-center leading-tight`}
                  style={{ color: cell.isRest ? '#6B6385' : '#2A2540' }}
                >
                  {figureShortLabel(cell.figureId)}
                </span>
              )}
              {state === 'hit' && !compact && <span className="text-lg mt-1" aria-hidden>✓</span>}
              {state === 'late' && !compact && (
                <span className="text-[10px] font-bold mt-1" style={{ color: '#F59E0B' }}>
                  Tarde
                </span>
              )}
              {state === 'miss' && !compact && (
                <span className="text-[10px] font-bold mt-1" style={{ color: '#F472B6' }}>
                  Perdido
                </span>
              )}
            </div>
            {showBar && !compact && (
              <div
                className="hidden sm:flex self-stretch w-1 rounded-full mx-0.5 shrink-0"
                style={{ background: `linear-gradient(180deg, ${ACCENT_HEX.sun}30, ${ACCENT_HEX.sun})` }}
                aria-hidden
                title="Fin de compás"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default FigureGrid;
